import { Injectable, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, catchError, debounceTime, distinctUntilChanged, filter, finalize, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { ApiService } from '@app/core/services/api.service';
import { AideArticle, AideEntree, AideEnveloppe, AideResultat, AideRubrique, AideSommaire, EtatAide, SLUG_AIDE } from './aide.models';
import { articlePourUrl, rubriquePourUrl } from './aide-ecran.util';

/** Le sommaire n'est pas redemandé plus souvent (l'API du caser le garde 1 h). */
const FRAICHEUR_SOMMAIRE_MS = 5 * 60 * 1000;

/**
 * Centre d'aide côté BO : sommaire en signal, tutos en cache (clé slug +
 * empreinte), recherche. Indisponible (TRP et copie de secours absents) :
 * l'état passe à `indisponible`, jamais d'erreur technique à l'écran.
 */
@Injectable({ providedIn: 'root' })
export class AideService {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  readonly sommaire = signal<AideSommaire | null>(null);
  readonly etat = signal<EtatAide>('chargement');
  /** Incrémenté pour ouvrir le panneau d'aide depuis un écran sans barre du BO (SOLO). */
  readonly demandeOuverture = signal(0);

  private readonly articles = new Map<string, AideArticle>();
  /** Tutos déjà notés pendant cette session : on ne repose pas la question. */
  private readonly avisDonnes = new Set<string>();
  private chargeLe = 0;
  private enCours: Observable<AideSommaire | null> | null = null;
  /** Dernier écran ouvert hors du centre d'aide : « Fermer » y ramène. */
  private ecranAvantAide: string | null = null;

  constructor() {
    this.retenirEcran(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.retenirEcran(e.urlAfterRedirects));
  }

  /**
   * Sommaire de ce caser, redemandé au plus toutes les 5 min. Un chargement en
   * cours est partagé. En cas d'échec : le sommaire déjà reçu, sinon null
   * (état `indisponible`).
   */
  chargerSommaire(force = false): Observable<AideSommaire | null> {
    const actuel = this.sommaire();
    if (!force && actuel && Date.now() - this.chargeLe < FRAICHEUR_SOMMAIRE_MS) {
      return of(actuel);
    }
    if (this.enCours) {
      return this.enCours;
    }
    if (!actuel) {
      this.etat.set('chargement');
    }
    this.enCours = this.api.get<AideEnveloppe<AideSommaire>>('monicase/aide').pipe(
      map((reponse) => reponse?.data ?? { rubriques: [], version: '' }),
      tap((sommaire) => {
        if (sommaire.version !== this.sommaire()?.version) {
          this.articles.clear();
        }
        this.sommaire.set(sommaire);
        this.chargeLe = Date.now();
        this.etat.set('pret');
      }),
      catchError(() => {
        this.etat.set(this.sommaire() ? 'pret' : 'indisponible');
        return of(this.sommaire());
      }),
      finalize(() => (this.enCours = null)),
      shareReplay(1),
    );
    return this.enCours;
  }

  /** null : tuto inexistant ou non ouvert à ce caser. Erreur : aide indisponible. */
  article(slug: string): Observable<AideArticle | null> {
    if (!SLUG_AIDE.test(slug)) {
      return of(null);
    }
    const cle = `${slug}|${this.entree(slug)?.empreinte ?? ''}`;
    const enCache = this.articles.get(cle);
    if (enCache) {
      return of(enCache);
    }
    return this.api.get<AideEnveloppe<AideArticle>>(`monicase/aide/articles/${slug}`).pipe(
      map((reponse) => reponse.data),
      tap((article) => this.articles.set(cle, article)),
      catchError((err: HttpErrorResponse) => (err?.status === 404 ? of(null) : throwError(() => err))),
    );
  }

  rechercher(requete: string): Observable<AideResultat[]> {
    const q = requete.trim();
    if (q.length < 2) {
      return of([]);
    }
    return this.api.get<AideEnveloppe<AideResultat[]>>('monicase/aide/recherche', { params: { q } }).pipe(
      map((reponse) => reponse?.data ?? []),
      catchError(() => of([])),
    );
  }

  /** Recherche au fil de la frappe : 250 ms de pause, 2 caractères minimum (null = pas de recherche). */
  resultats(saisies: Observable<string>): Observable<AideResultat[] | null> {
    return saisies.pipe(
      map((q) => q.trim()),
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((q) => (q.length < 2 ? of(null) : this.rechercher(q))),
    );
  }

  /** Quitte le centre d'aide : retour à l'écran d'avant, sinon au lanceur d'applications. */
  fermer(): void {
    this.router.navigateByUrl(this.ecranAvantAide ?? '/launcher');
  }

  ouvrirPanneau(): void {
    this.demandeOuverture.update((n) => n + 1);
  }

  /** « Ça vous a aidé ? ». Erreur : avis non transmis (TRP injoignable). */
  donnerAvis(slug: string, utile: boolean, commentaire?: string): Observable<void> {
    const texte = commentaire?.trim() || null;
    return this.api.post<AideEnveloppe<null>>(`monicase/aide/articles/${slug}/avis`, { utile, commentaire: texte }).pipe(
      tap(() => this.avisDonnes.add(slug)),
      map(() => undefined),
    );
  }

  avisDonne(slug: string): boolean {
    return this.avisDonnes.has(slug);
  }

  articlePourUrl(url: string): AideEntree | null {
    return articlePourUrl(url, this.sommaire());
  }

  rubriquePourUrl(url: string): AideRubrique | null {
    const cle = rubriquePourUrl(url);
    return this.sommaire()?.rubriques.find((r) => r.cle === cle) ?? null;
  }

  rubrique(cle: string): AideRubrique | null {
    return this.sommaire()?.rubriques.find((r) => r.cle === cle) ?? null;
  }

  private entree(slug: string): AideEntree | undefined {
    for (const rubrique of this.sommaire()?.rubriques ?? []) {
      const article = rubrique.articles.find((a) => a.slug === slug);
      if (article) {
        return article;
      }
    }
    return undefined;
  }

  private retenirEcran(url: string): void {
    if (url && url !== '/' && !/^\/aide(\/|\?|#|$)/.test(url)) {
      this.ecranAvantAide = url;
    }
  }
}
